<template>
  <div class="panel-screen">
    <panel-toolbar />

    <b-skeleton-wrapper :loading="loadingWallet">
      <template #loading>
        <hb-section><b-skeleton-img no-aspect height="180px" /></hb-section>
      </template>

      <section-error v-if="walletError" :message="walletError" @retry="loadWallet" />

      <wallet-summary-card
        v-else-if="balances"
        :balances="balances"
        :upcoming="upcoming"
        @withdraw="openWithdraw"
      />
    </b-skeleton-wrapper>

    <hb-tabs :value="tab" :tabs="tabs" @input="changeTab" />

    <!-- Extrato -->
    <template v-if="tab === 'extrato'">
      <b-skeleton-wrapper :loading="loadingEntries">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="200px" /></hb-section>
        </template>

        <section-error v-if="entriesError" :message="entriesError" @retry="loadEntries" />

        <statement-pane
          v-else
          :entries="entries"
          :totals="entryTotals"
          :type="entryType"
          :current-page="page"
          :last-page="lastPage"
          :total="totalEntries"
          :per-page="perPage"
          @update:type="changeEntryType"
          @update:page="changePage"
        />
      </b-skeleton-wrapper>
    </template>

    <!-- Saques -->
    <template v-else-if="tab === 'saques'">
      <b-skeleton-wrapper :loading="loadingWithdrawals">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="200px" /></hb-section>
        </template>

        <section-error v-if="withdrawalsError" :message="withdrawalsError" @retry="loadWithdrawals" />

        <withdrawals-pane
          v-else
          :withdrawals="withdrawals"
          @request="openWithdraw"
          @cancel="confirmCancel"
          @receipt="downloadReceipt"
        />
      </b-skeleton-wrapper>
    </template>

    <!-- Chaves PIX -->
    <template v-else>
      <b-skeleton-wrapper :loading="loadingPix">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="160px" /></hb-section>
        </template>

        <section-error v-if="pixError" :message="pixError" @retry="loadPixKeys" />

        <pix-keys-pane
          v-else
          :keys="pixKeys"
          @create="$refs.pixForm.open()"
          @set-default="setDefaultPixKey"
          @remove="confirmRemovePixKey"
        />
      </b-skeleton-wrapper>
    </template>

    <withdrawal-request-modal
      ref="withdraw"
      :max-cents="balances ? balances.withdrawable : 0"
      :pix-keys="pixKeys"
      @confirm="requestWithdrawal"
    />

    <pix-key-form-modal ref="pixForm" @save="savePixKey" />
  </div>
</template>

<script>
import { BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'
import InfluencerPanelService from '@/services/api/InfluencerPanelService'
import HbSection from '@/components/hubee/HbSection.vue'
import HbTabs from '@/components/hubee/HbTabs.vue'
import PanelToolbar from './components/PanelToolbar.vue'
import WalletSummaryCard from './components/WalletSummaryCard.vue'
import StatementPane from './components/StatementPane.vue'
import WithdrawalsPane from './components/WithdrawalsPane.vue'
import PixKeysPane from './components/PixKeysPane.vue'
import WithdrawalRequestModal from './components/WithdrawalRequestModal.vue'
import PixKeyFormModal from './components/PixKeyFormModal.vue'
import SectionError from './components/SectionError.vue'
import { extractMessage } from './panel'

const TABS = ['extrato', 'saques', 'pix']

/**
 * Minha Carteira.
 *
 * As chaves PIX sao carregadas junto da carteira, e nao so quando a aba abre: o modal de
 * saque precisa delas para saber se ha destino valido, e ele pode ser aberto a partir do
 * card de saldo, sem passar pela aba.
 */
export default {
  components: {
    BSkeletonImg,
    BSkeletonWrapper,
    HbSection,
    HbTabs,
    PanelToolbar,
    WalletSummaryCard,
    StatementPane,
    WithdrawalsPane,
    PixKeysPane,
    WithdrawalRequestModal,
    PixKeyFormModal,
    SectionError,
  },
  data: () => ({
    service: null,
    tab: 'extrato',
    balances: null,
    upcoming: [],
    entries: [],
    entryTotals: { inflow_cents: 0, outflow_cents: 0 },
    entryType: '',
    page: 1,
    lastPage: 1,
    totalEntries: 0,
    perPage: 30,
    withdrawals: [],
    pixKeys: [],
    loadingWallet: true,
    loadingEntries: true,
    loadingWithdrawals: false,
    loadingPix: true,
    loadedWithdrawals: false,
    walletError: '',
    entriesError: '',
    withdrawalsError: '',
    pixError: '',
  }),
  computed: {
    tabs() {
      return [
        { key: 'extrato', label: this.$t('influencer_panel.tab_statement') },
        { key: 'saques', label: this.$t('influencer_panel.tab_withdrawals') },
        { key: 'pix', label: this.$t('influencer_panel.tab_pix'), count: this.pixKeys.length },
      ]
    },
  },
  beforeMount() {
    this.service = InfluencerPanelService.build(this.$vs)

    if (TABS.includes(this.$route.query.tab)) this.tab = this.$route.query.tab
  },
  mounted() {
    this.loadWallet()
    this.loadPixKeys()
    this.ensureTabLoaded()

    // O Resumo manda para cá com `?withdraw=1` no botão "Solicitar saque". Espera as
    // chaves chegarem: sem elas o modal abriria dizendo que não há destino válido.
    if (this.$route.query.withdraw === '1') {
      Promise.all([this.loadWallet(), this.loadPixKeys()]).then(() => this.openWithdraw())
    }
  },
  methods: {
    loadWallet() {
      this.loadingWallet = true
      this.walletError = ''

      return this.service.wallet()
        .then(data => {
          this.balances = data.balances
          this.upcoming = data.upcoming || []
        })
        .catch(error => { this.walletError = extractMessage(error) })
        .finally(() => { this.loadingWallet = false })
    },
    loadEntries() {
      this.loadingEntries = true
      this.entriesError = ''

      return this.service.walletEntries({ page: this.page, type: this.entryType })
        .then(result => {
          this.entries = result.data || []
          this.entryTotals = result.totals || { inflow_cents: 0, outflow_cents: 0 }
          this.lastPage = result.last_page || 1
          this.totalEntries = result.total || 0
          this.perPage = result.per_page || 30
        })
        .catch(error => { this.entriesError = extractMessage(error) })
        .finally(() => { this.loadingEntries = false })
    },
    loadWithdrawals() {
      this.loadingWithdrawals = true
      this.withdrawalsError = ''
      this.loadedWithdrawals = true

      return this.service.withdrawals()
        .then(list => { this.withdrawals = list || [] })
        .catch(error => { this.withdrawalsError = extractMessage(error) })
        .finally(() => { this.loadingWithdrawals = false })
    },
    loadPixKeys() {
      this.loadingPix = true
      this.pixError = ''

      return this.service.pixKeys()
        .then(list => { this.pixKeys = list || [] })
        .catch(error => { this.pixError = extractMessage(error) })
        .finally(() => { this.loadingPix = false })
    },
    ensureTabLoaded() {
      if (this.tab === 'extrato') this.loadEntries()
      if (this.tab === 'saques' && !this.loadedWithdrawals) this.loadWithdrawals()
    },
    changeTab(tab) {
      this.tab = tab
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {})
      this.ensureTabLoaded()
    },
    changeEntryType(type) {
      this.entryType = type
      this.page = 1
      this.loadEntries()
    },
    changePage(page) {
      this.page = page
      this.loadEntries()
    },
    openWithdraw() {
      this.$refs.withdraw.open()
    },
    requestWithdrawal({ amountCents, pixKeyId }) {
      this.$vs.loading()
      this.service.requestWithdrawal(amountCents, pixKeyId)
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('influencer_panel.request_withdrawal'))
          this.$refs.withdraw.close()
          this.loadWallet()
          this.loadWithdrawals()
          if (this.tab === 'extrato') this.loadEntries()
        })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
        .finally(() => { this.$vs.loading.close() })
    },
    confirmCancel(withdrawal) {
      this.$bvModal.msgBoxConfirm(this.$t('influencer_panel.cancel_withdrawal_confirm'), {
        title: this.$t('action.cancel'),
        okVariant: 'danger',
        okTitle: this.$t('action.confirm'),
        cancelTitle: this.$t('action.go_back'),
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(confirmed => {
        if (!confirmed) return

        this.$vs.loading()
        this.service.cancelWithdrawal(withdrawal.id)
          .then(() => {
            this.notifySuccess(this.$vs, this.$t('action.cancel'))
            this.loadWallet()
            this.loadWithdrawals()
          })
          .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
          .finally(() => { this.$vs.loading.close() })
      })
    },
    downloadReceipt(withdrawal) {
      this.service.withdrawalReceipt(withdrawal.id)
        .then(receipt => { window.open(receipt.url, '_blank', 'noopener') })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'warning' }) })
    },
    savePixKey({ keyType, keyValue }) {
      this.$vs.loading()
      this.service.savePixKey(keyType, keyValue)
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('save'))
          this.$refs.pixForm.close()
          this.loadPixKeys()
        })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
        .finally(() => { this.$vs.loading.close() })
    },
    setDefaultPixKey(key) {
      this.$vs.loading()
      this.service.setDefaultPixKey(key.id)
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('influencer_panel.pix_make_default'))
          this.loadPixKeys()
        })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
        .finally(() => { this.$vs.loading.close() })
    },
    confirmRemovePixKey(key) {
      this.$bvModal.msgBoxConfirm(this.$t('influencer_panel.pix_remove_confirm'), {
        title: this.$t('influencer_panel.pix_remove'),
        okVariant: 'danger',
        okTitle: this.$t('action.remove'),
        cancelTitle: this.$t('action.cancel'),
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(confirmed => {
        if (!confirmed) return

        this.$vs.loading()
        this.service.deletePixKey(key.id)
          .then(() => {
            this.notifySuccess(this.$vs, this.$t('action.remove'))
            this.loadPixKeys()
          })
          .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
          .finally(() => { this.$vs.loading.close() })
      })
    },
  },
}
</script>

<style scoped>
.panel-screen {
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 1.8vw, 1.5rem);
}
</style>
